import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageSquare, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const PortalMessagesCard = ({ customerId }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/customers/${customerId}/portal-messages`);
      setMessages(res.data || []);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) fetchMessages();
  }, [customerId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await api.post(`/customers/${customerId}/portal-messages`, { message: newMsg });
      setNewMsg('');
      fetchMessages();
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="portal-messages-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-500" />
          Portal Messages
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <div className="max-h-[250px] overflow-y-auto space-y-2 mb-3 p-2 bg-slate-50 rounded-lg">
              {messages.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No messages yet</p>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.is_customer ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[85%] rounded-xl px-3 py-1.5 text-xs ${
                      msg.is_customer
                        ? 'bg-white border border-slate-200'
                        : 'bg-blue-600 text-white'
                    }`}>
                      {msg.is_customer && (
                        <span className="font-semibold text-blue-600 block mb-0.5">{msg.sender_name}</span>
                      )}
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-[9px] mt-0.5 ${msg.is_customer ? 'text-slate-400' : 'text-blue-200'}`}>
                        {formatDate(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={endRef} />
            </div>
            <div className="flex gap-1.5">
              <Input
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                placeholder="Reply to customer..."
                className="text-sm h-8"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                data-testid="staff-message-input"
              />
              <Button
                size="sm"
                className="h-8 px-3 bg-blue-600 hover:bg-blue-700"
                onClick={handleSend}
                disabled={sending || !newMsg.trim()}
                data-testid="staff-send-msg-btn"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
